import type { MapDef } from '../world/map';
import { T } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the taroko package. Tile chars reserved for it: 'x', '!' walkable, '?' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker too,
 * so keep it pure: no DOM, no game state. Return null for chars you don't own.
 *
 *  'x'  marble flagstones (trails, plazas, the shrine ledge); stone steps inside STAIRS; planks on the pier;
 *       Qixingtan's grey-blue pebbles inside BEACH
 *  '!'  the Qixingtan cycle path: red asphalt with white lines and bike symbols
 *  '?'  forested mountainside; inside SHAKADANG it is the crystal-clear blue-green stream
 */

/** Tile rects (on the hualien map) drawn as stone steps. */
const STAIRS: readonly [number, number, number, number][] = [
  [41, 5, 2, 6], // shrine → bell tower
  [42, 22, 2, 3], // road → Buluowan
  [49, 22, 2, 3], // road → Shakadang trail
  [48, 24, 1, 3],
];
/** Tile rects drawn as wooden planks. */
const PLANKS: readonly [number, number, number, number][] = [[86, 44, 8, 1]];
/** Qixingtan beach: its 'x' tiles are pebbles. */
const BEACH: readonly [number, number, number, number] = [76, 19, 24, 29];
/** The Shakadang stream (its '?' tiles are water, not forest). */
const SHAKADANG: readonly [number, number, number, number] = [51, 22, 3, 23];

const inRect = (tx: number, ty: number, r: readonly [number, number, number, number]) => tx >= r[0] && ty >= r[1] && tx < r[0] + r[2] && ty < r[1] + r[3];

const at = (map: MapDef, tx: number, ty: number) => (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h ? ' ' : map.rows[ty][tx]);

const K = {
  marbleA: hex('#e4e0d6'),
  marbleB: hex('#cfcac0'),
  joint: hex('#9a948a'),
  vein: hex('#8a8a90'),
  bikeA: hex('#a8483e'),
  bikeB: hex('#b85a4c'),
  line: hex('#f4efe6'),
  forestDark: hex('#1f4a2a'),
  forestMid: hex('#2f6a36'),
  forestLight: hex('#5a9a4a'),
  forestSun: hex('#8ac060'),
  waterShallow: hex('#8fe0d4'),
  waterDeep: hex('#1f9fb0'),
  plank: hex('#a8784a'),
};

/** Running-bond marble flagstones, with a faint grey vein now and then. */
function marble(x: number, y: number): RGB {
  const rowH = 6;
  const row = Math.floor(y / rowH);
  const w = 9;
  const off = (row % 2) * 4.5;
  const col = Math.floor((x + off) / w);
  const fx = x + off - col * w, fy = y - row * rowH;
  const tone = hash2(col, row, 401);
  let c = mix(K.marbleA, K.marbleB, tone * 0.7 + noise2(x * 0.3, y * 0.3, 402) * 0.3);
  const v = Math.abs(Math.sin((x * 0.7 + y * 0.9) / 5 + fbm(x / 10, y / 10, 403) * 6));
  if (tone > 0.55 && v < 0.06) c = mix(c, K.vein, 0.35);
  if (fx < 0.7 || fy < 0.7) c = mix(c, K.joint, 0.75);
  else if (fy < 1.4) c = scale(c, 1.06);
  return c;
}

/** Stone steps: a lit tread and a shaded riser every 4px. */
function steps(x: number, y: number): RGB {
  const fy = ((y % 4) + 4) % 4;
  let c = mix(K.marbleA, K.marbleB, noise2(x * 0.4, y * 0.2, 411));
  if (fy < 0.8) c = scale(c, 1.08);
  else if (fy > 2.9) c = scale(c, 0.7);
  if (Math.floor(x) % 16 === 0) c = scale(c, 0.85);
  return c;
}

function planks(x: number, y: number): RGB {
  const fx = ((x % 4) + 4) % 4;
  let c = scale(K.plank, 0.92 + hash2(Math.floor(x / 4), 0, 421) * 0.16);
  if (fx < 0.6) c = scale(c, 0.6);
  return scale(c, 0.95 + noise2(x * 0.5, y * 2, 422) * 0.1);
}

/** Red cycle path: white edge lines, a dashed centre line, a bike symbol every few tiles. */
function cyclePath(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  let c = mix(K.bikeA, K.bikeB, noise2(x * 0.6, y * 0.6, 431));
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 432) < 0.06) c = scale(c, 0.88);
  const leftOpen = at(map, tx - 1, ty) === '!', rightOpen = at(map, tx + 1, ty) === '!';
  if (!leftOpen && fx > 1.2 && fx < 2.2) return K.line;
  if (!rightOpen && fx > T - 2.2 && fx < T - 1.2) return K.line;
  if (rightOpen && fx > T - 0.5 && fy % 8 < 4) return K.line;
  if (leftOpen && fx < 0.5 && fy % 8 < 4) return K.line;
  // A bike pictogram in the left lane every 6 tiles.
  if (!leftOpen && ty % 6 === 2) {
    const cx = T / 2 + 0.5, cy = T / 2;
    const r1 = Math.hypot(fx - (cx - 3.4), fy - (cy + 1.4)), r2 = Math.hypot(fx - (cx + 3.4), fy - (cy + 1.4));
    if (Math.abs(r1 - 2.2) < 0.45 || Math.abs(r2 - 2.2) < 0.45) return K.line;
    // Frame: a triangle between the wheels and the saddle.
    const onSeg = (ax: number, ay: number, bx: number, by: number) => {
      const dx = bx - ax, dy = by - ay;
      const k = Math.max(0, Math.min(1, ((fx - ax) * dx + (fy - ay) * dy) / (dx * dx + dy * dy)));
      return Math.hypot(fx - (ax + dx * k), fy - (ay + dy * k)) < 0.45;
    };
    if (onSeg(cx - 3.4, cy + 1.4, cx, cy + 1.4) || onSeg(cx, cy + 1.4, cx + 2, cy - 1.6) || onSeg(cx - 1.2, cy - 1.6, cx + 2, cy - 1.6) || onSeg(cx - 3.4, cy + 1.4, cx - 1.2, cy - 1.6) || onSeg(cx + 2, cy - 1.6, cx + 3.4, cy + 1.4) || onSeg(cx + 2, cy - 1.6, cx + 2.6, cy - 3)) return K.line;
  }
  return c;
}

/** Forest canopy seen from above: round crowns, lit from the top left, darker where it meets rock. */
function forest(map: MapDef, x: number, y: number): RGB {
  const s = 7;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  let pick: RGB | null = null, top = -1e9;
  for (let j = -1; j <= 1; j++) {
    for (let i = -1; i <= 1; i++) {
      const gx = cx + i, gy = cy + j;
      const px = (gx + 0.5 + (hash2(gx, gy, 441) - 0.5) * 0.8) * s, py = (gy + 0.5 + (hash2(gx, gy, 442) - 0.5) * 0.8) * s;
      const r = s * (0.62 + hash2(gx, gy, 443) * 0.3);
      const d = Math.hypot(x - px, y - py) / r;
      // Crowns further south are drawn over northern ones (pick the lowest centre that covers the pixel).
      if (d < 1 && py > top) {
        top = py;
        const lx = (x - px) / r, ly = (y - py) / r;
        const lit = smoothstep(0.6, -0.6, lx + ly);
        let c = mix(K.forestMid, K.forestLight, lit * 0.8);
        if (lx + ly < -0.8 && d < 0.7) c = mix(c, K.forestSun, 0.35);
        c = mix(c, K.forestDark, smoothstep(0.75, 1, d) * 0.7);
        const hue = hash2(gx, gy, 444);
        if (hue < 0.12) c = mix(c, hex('#6a8a3a'), 0.35);
        else if (hue > 0.92) c = mix(c, hex('#3a5a5a'), 0.3);
        pick = c;
      }
    }
  }
  let c: RGB = pick ?? scale(K.forestDark, 0.8);
  // Shade along the bottom edge where the slope falls to rock or a path.
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fy = y - ty * T;
  if (at(map, tx, ty + 1) !== '?') c = scale(c, 1 - smoothstep(T - 6, T, fy) * 0.35);
  if (at(map, tx, ty - 1) !== '?' && at(map, tx, ty - 1) !== ' ') c = mix(c, K.forestSun, smoothstep(3, 0, fy) * 0.25);
  return scale(c, 0.94 + noise2(x * 0.7, y * 0.7, 445) * 0.1);
}

const STONES = ['#8f9aa3', '#a9b2b8', '#7a858e', '#c3c8cb', '#66717a', '#9aa0a0'].map(hex);

/** Qixingtan: rounded grey-blue pebbles, darker and glossy where the waves wet them. */
function beachPebbles(map: MapDef, x: number, y: number): RGB {
  const s = 4.2;
  const gy = Math.floor(y / s), off = (gy % 2) * s * 0.5;
  const gx = Math.floor((x + off) / s);
  const pcx = (gx + 0.5) * s - off + (hash2(gx, gy, 461) - 0.5) * 1.2, pcy = (gy + 0.5) * s + (hash2(gx, gy, 462) - 0.5) * 1.2;
  const rx = s * (0.42 + hash2(gx, gy, 463) * 0.14), ry = rx * 0.78;
  const d = Math.hypot((x - pcx) / rx, (y - pcy) / ry);
  let c = STONES[Math.floor(hash2(gx, gy, 464) * STONES.length)];
  if (d > 1) c = mix(c, hex('#4f5860'), 0.55);
  else if (d < 0.55 && y < pcy && x < pcx) c = mix(c, hex('#eef2f4'), 0.3);
  else if (d > 0.8) c = scale(c, 0.88);
  c = mix(c, hex('#8a8f8c'), fbm(x / 40, y / 40, 465) * 0.2);
  // Wet band near the water line.
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T;
  const wet = at(map, tx + 1, ty) === 'Z' ? smoothstep(4, T, fx) : 0;
  if (wet > 0) c = mix(scale(c, 0.72), hex('#3a5a6a'), wet * 0.25);
  return c;
}

/** Shakadang: water so clear you see the pebbles on the bottom; blue-green in the middle. */
function clearStream(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T;
  const left = at(map, tx - 1, ty) !== '?', right = at(map, tx + 1, ty) !== '?';
  let d = 40;
  if (left) d = Math.min(d, fx);
  if (right) d = Math.min(d, T - fx);
  if (at(map, tx - 2, ty) !== '?') d = Math.min(d, fx + T);
  if (at(map, tx + 2, ty) !== '?') d = Math.min(d, 2 * T - fx);
  d += (noise2(x * 0.25, y * 0.25, 451) - 0.5) * 2;
  if (d < 1.2) return hex('#f0fbf8');
  // Pebbles on the bottom, tinted by the water.
  const ps = 3;
  const pcx = Math.floor(x / ps), pcy = Math.floor(y / ps);
  const pd = Math.hypot(x - (pcx + 0.5) * ps, y - (pcy + 0.5) * ps) / (ps * 0.6);
  const ptone = hash2(pcx, pcy, 452);
  let bottom = mix(hex('#c8c4b4'), hex('#8a8a7a'), ptone);
  if (pd > 1) bottom = scale(bottom, 0.7);
  const depth = smoothstep(1, 18, d);
  let c = mix(mix(bottom, K.waterShallow, 0.45), K.waterDeep, depth * 0.75);
  const flow = fbm(x / 6, y / 22 - 0, 453);
  if (flow > 0.66) c = mix(c, hex('#f4fffc'), (flow - 0.66) * 2);
  const glint = fbm(x / 3, y / 9, 454);
  if (glint > 0.72) c = mix(c, hex('#ffffff'), 0.25);
  return c;
}

export function colorAtTaroko(map: MapDef, x: number, y: number, mat: string): RGB | null {
  if (mat !== 'x' && mat !== '!' && mat !== '?') return null;
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const here = map.id === 'hualien';
  switch (mat) {
    case 'x':
      if (here && STAIRS.some((r) => inRect(tx, ty, r))) return steps(x, y);
      if (here && PLANKS.some((r) => inRect(tx, ty, r))) return planks(x, y);
      if (here && inRect(tx, ty, BEACH)) return beachPebbles(map, x, y);
      return marble(x, y);
    case '!':
      return cyclePath(map, x, y);
    default:
      if (here && inRect(tx, ty, SHAKADANG)) return clearStream(map, x, y);
      return forest(map, x, y);
  }
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_TAROKO: Record<string, string> = { x: '#dcd8ce', '!': '#ad5044', '?': '#2f6a36' };
